// Mock pricing quote — the disposable stand-in for the backend pricing module's
// `resolve_price` action. Given a configured item + price group (+ optional
// runtime scenario), it assembles an attributed breakdown by applying the rule
// set's factors on top of the base: market and commitment (standing), the Speed
// price rule and capacity (order-time), then guardrails. Each line carries a `why` so the
// admin worked example can illustrate the full derivation. Consumers render the
// PriceQuote and never compute factors themselves. Replaced by a DataAdapter
// call to the pricing endpoint when the module lands.

import type {
  PriceQuote,
  PriceQuoteRequest,
  PriceScenario,
  QuoteLine,
  QuoteVerdict,
} from "../data/pricing-quote";
import type { CatalogueItem } from "../data/catalogue-types";
import type {
  PriceGroup,
  Stance,
  StanceSetting,
  CapacityBand,
  CapacityConfig,
} from "../data/pricing-admin-types";
import { getMockCatalogueItem } from "./catalogue";
import { getMockPriceGroup } from "./pricing";
import { priceForQuantity } from "../domain/catalogue-pricing";

const roundGbp = (n: number) => Math.round(n);
const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

/** Guardrails rounding — always rounds UP to the step, or leaves the figure as-is. */
function applyRounding(value: number, rounding: string): number {
  if (rounding === "none") return Math.round(value * 100) / 100;
  const step = rounding === "£10" ? 10 : rounding === "£5" ? 5 : 1;
  return Math.ceil(value / step) * step;
}
const pctLabel = (f: number) =>
  `${f >= 0 ? "+" : "−"}${Math.round(Math.abs(f) * 100)}%`;
const signedGbp = (n: number) =>
  `${n < 0 ? "−" : "+"}£${Math.abs(n).toLocaleString()}`;

// ── Market ──────────────────────────────────────────────────────────────────

const STANCE_DISPLAY: Record<Stance, string> = {
  hold: "Neutral",
  win: "Win it",
  track: "Track market",
  premium: "Premium",
  optimise: "Maximise",
};

export const stanceLabel = (s: Stance): string => STANCE_DISPLAY[s];

/** The stance that applies to this item: its exception, else the group default. */
export function resolveStanceSetting(
  priceGroup: PriceGroup,
  itemId: string,
): { setting: StanceSetting; source: string } {
  const ex = priceGroup.market.exceptions.find((e) => e.itemId === itemId);
  if (ex) return { setting: ex, source: ex.itemLabel };
  return { setting: priceGroup.market.defaultStance, source: "default" };
}

export interface MarketRec {
  /** Recommended total at this quantity. */
  price: number;
  /** Plain-English derivation. */
  why: string;
  /** Optimise only — the win-probability curve points. */
  curve?: { price: number; win: number }[];
  /** True when the stance had to fall back (no competitor / thin win data). */
  lowConfidence?: boolean;
}

/** Mock win probability at a price, anchored so the competitor reference ≈ 60%. */
function winProbAt(price: number, competitor: number): number {
  const rel = (price - competitor) / competitor;
  return clamp(0.6 - rel * 2.2, 0.02, 0.98);
}

/**
 * Turn a stance + the two signals into a recommended price. `costPlus` is P₀,
 * `competitor` is C. Win rate only enters Optimise (via the curve); the other
 * stances anchor on C. Falls back to Hold with no competitor, to Track when
 * Optimise has no cost. Guardrails bound the result downstream.
 */
export function marketRecommendation(
  setting: StanceSetting,
  costPlus: number,
  competitor: number | null,
  cost?: number,
): MarketRec {
  if (competitor == null) {
    return {
      price: costPlus,
      why: "No competitor data — held at cost-plus",
      lowConfidence: true,
    };
  }
  const c = Math.round(competitor);
  switch (setting.stance) {
    case "hold":
      return {
        price: costPlus,
        why: "Hold — our price stands; market shown for context",
      };
    case "track":
      return {
        price: roundGbp(competitor),
        why: `Track — match the £${c} market reference`,
      };
    case "win": {
      const d = setting.dial ?? 0.06;
      return {
        price: roundGbp(competitor * (1 - d)),
        why: `Win — sit ${Math.round(d * 100)}% below the £${c} market`,
      };
    }
    case "premium": {
      const p = setting.dial ?? 0.05;
      return {
        price: roundGbp(competitor * (1 + p)),
        why: `Premium — hold ${Math.round(p * 100)}% above the £${c} market`,
      };
    }
    case "optimise": {
      if (cost == null) {
        return {
          price: roundGbp(competitor),
          why: "Optimise — win data thin, tracking the market instead",
          lowConfidence: true,
        };
      }
      const lean = setting.lean ?? 0; // −1 margin .. +1 share
      const expo = Math.max(0, 1 + lean); // 0 = ignore win (margin) .. 2 = weight win (share)
      const curve = [-0.08, -0.04, 0, 0.04, 0.08].map((r) => {
        const price = roundGbp(competitor * (1 + r));
        return { price, win: winProbAt(price, competitor) };
      });
      let best = curve[0];
      let bestScore = -Infinity;
      for (const pt of curve) {
        const score = (pt.price - cost) * Math.pow(pt.win, expo);
        if (score > bestScore) {
          bestScore = score;
          best = pt;
        }
      }
      return {
        price: best.price,
        why: "Optimise — peak expected contribution",
        curve,
      };
    }
  }
}

// ── Commitment ──────────────────────────────────────────────────────────────

function commitmentFor(
  priceGroup: PriceGroup,
  productLabel: string,
): { rate: number; productType: string } | null {
  const c = priceGroup.commitment;
  if (!c) return null;
  const specific = c.discountExceptions.find((d) =>
    productLabel.toLowerCase().includes(d.productType.toLowerCase()),
  );
  const rate = specific ? specific.pct : c.discountDefaultPct;
  if (rate <= 0) return null;
  return {
    rate,
    productType: specific ? specific.productType : "All products",
  };
}

export interface RebateSlice {
  /** Bracket bounds actually filled by this spend. */
  from: number;
  to: number;
  pct: number;
  amount: number;
}

/** Brackets in evaluation order: finite ascending by upper bound, open band last. */
export function orderRebateBands<T extends { upTo: number | null }>(
  bands: T[],
): T[] {
  const finite = bands
    .filter((b) => b.upTo != null)
    .sort((a, b) => (a.upTo as number) - (b.upTo as number));
  const open = bands.filter((b) => b.upTo == null);
  return [...finite, ...open];
}

/**
 * Incremental (tax-bracket) rebate on a cumulative annual spend. Each bracket's
 * rate applies only to the slice of spend within it (lower bound = previous
 * bracket's upper bound, £0 for the first), then the slices sum. Shared by the
 * resolver and the admin example box so the formula has one definition.
 */
export function rebateBreakdown(
  bands: { upTo: number | null; pct: number }[],
  spend: number,
): { slices: RebateSlice[]; earned: number; effectivePct: number } {
  const slices: RebateSlice[] = [];
  let earned = 0;
  let from = 0;
  for (const band of orderRebateBands(bands)) {
    if (spend <= from) break;
    const to = band.upTo ?? Infinity;
    const upper = Math.min(spend, to);
    const amount = (upper - from) * band.pct;
    slices.push({ from, to: upper, pct: band.pct, amount });
    earned += amount;
    from = to;
  }
  const effectivePct = spend > 0 ? earned / spend : 0;
  return { slices, earned, effectivePct };
}

/** Retrospective rebate earned at the current spend (illustrated, not in the quoted total). */
function rebateFor(
  priceGroup: PriceGroup,
  spendToDate: number | undefined,
): { earned: number; effectivePct: number; period: string } | null {
  const r = priceGroup.commitment?.rebate;
  if (!r || spendToDate == null || spendToDate <= 0) return null;
  const { earned, effectivePct } = rebateBreakdown(r.bands, spendToDate);
  if (earned <= 0) return null;
  return { earned: Math.round(earned), effectivePct, period: r.period };
}

// ── Capacity ──────────────────────────────────────────────────────────────

/** The production areas the capacity factor can price, in display order. */
export const CAPACITY_AREAS = [
  "Litho",
  "Digital",
  "Finishing",
  "Large format",
] as const;

/**
 * Rough item → production-area mapping with each area's share of the job cost.
 * Stand-in for the estimate's per-area cost breakdown (shares sum to 1); the
 * capacity adjustment applies to each area's cost share, then the areas sum.
 */
export function capacityAreasFor(
  item: CatalogueItem,
): { area: string; costShare: number }[] {
  const l = item.productLabel.toLowerCase();
  if (l.includes("large format") || l.includes("banner"))
    return [{ area: "Large format", costShare: 1 }];
  if (
    l.includes("brochure") ||
    l.includes("book") ||
    l.includes("leaflet") ||
    l.includes("folder")
  ) {
    return [
      { area: "Litho", costShare: 0.6 },
      { area: "Finishing", costShare: 0.4 },
    ];
  }
  return [{ area: "Digital", costShare: 1 }];
}

/** The bands that apply to an area: its override if present, else the default. */
export function resolveCapacityBands(
  config: CapacityConfig,
  area: string,
): CapacityBand[] {
  return (
    config.overrides.find((o) => o.area === area)?.bands ?? config.defaultBands
  );
}

/** Signed adjustment fraction for a band (premium +, discount −, none 0). */
export function capacityBandFraction(band: CapacityBand): number {
  return band.kind === "premium"
    ? band.pct
    : band.kind === "discount"
      ? -band.pct
      : 0;
}

/** The band a given load falls in (bands tile 0..1; last band ends at 1.0). */
export function capacityBandForLoad(
  bands: CapacityBand[],
  load: number,
): { band: CapacityBand; from: number } {
  const sorted = [...bands].sort((a, b) => a.upTo - b.upTo);
  let from = 0;
  for (const band of sorted) {
    if (load <= band.upTo) return { band, from };
    from = band.upTo;
  }
  const last = sorted[sorted.length - 1];
  return {
    band: last,
    from: sorted.length > 1 ? sorted[sorted.length - 2].upTo : 0,
  };
}

export interface CapacityAreaRow {
  area: string;
  costShare: number;
  areaCost: number;
  load: number;
  from: number;
  upTo: number;
  fraction: number;
  amount: number;
}

/**
 * Per-area capacity adjustment: each area's band adjustment applies to that
 * area's share of the job cost, and the areas sum. It is a cost-relative factor,
 * so no cost ⇒ no capacity move.
 */
function capacityMove(
  priceGroup: PriceGroup,
  item: CatalogueItem,
  load: Record<string, number> | undefined,
  totalCost: number | null,
): { total: number; rows: CapacityAreaRow[] } | null {
  const cfg = priceGroup.capacity;
  if (!cfg.enabled || !load || totalCost == null || totalCost <= 0) return null;
  const rows: CapacityAreaRow[] = [];
  let total = 0;
  for (const { area, costShare } of capacityAreasFor(item)) {
    const util = load[area];
    if (util == null) continue;
    const { band, from } = capacityBandForLoad(
      resolveCapacityBands(cfg, area),
      util,
    );
    const fraction = capacityBandFraction(band);
    const areaCost = roundGbp(totalCost * costShare);
    const amount = roundGbp(areaCost * fraction);
    total += amount;
    rows.push({
      area,
      costShare,
      areaCost,
      load: util,
      from,
      upTo: band.upTo,
      fraction,
      amount,
    });
  }
  if (!rows.length) return null;
  return { total, rows };
}

// ── Guardrails ──────────────────────────────────────────────────────────────

/** Pull the first number out of a guardrail string ("8%", "±5%", "cost (0%)"). */
function parseLimit(s: string): number {
  const m = s.match(/(\d+(?:\.\d+)?)/);
  return m ? Number(m[1]) / 100 : 0;
}

function guardrailVerdict(
  priceGroup: PriceGroup,
  quoted: number,
  quantity: number,
  scenario: PriceScenario,
  concessionFraction: number,
): QuoteVerdict | undefined {
  const rows = priceGroup.guardrails.rows;
  const checks: { status: "approval" | "blocked"; message: string }[] = [];
  let marginPct: number | undefined;

  const marginRow = rows.find((r) =>
    r.name.toLowerCase().includes("contribution"),
  );
  if (marginRow && scenario.unitCost != null && quoted > 0) {
    const cost = scenario.unitCost * quantity;
    marginPct = (quoted - cost) / quoted;
    const approval = parseLimit(marginRow.approval);
    const hard = parseLimit(marginRow.hard);
    if (marginPct < hard)
      checks.push({
        status: "blocked",
        message: `Contribution ${Math.round(marginPct * 100)}% below the ${Math.round(hard * 100)}% floor`,
      });
    else if (marginPct < approval)
      checks.push({
        status: "approval",
        message: `Contribution ${Math.round(marginPct * 100)}% below the ${Math.round(approval * 100)}% approval line`,
      });
  }

  const concessionRow = rows.find((r) =>
    r.name.toLowerCase().includes("concession"),
  );
  if (concessionRow && concessionFraction > 0) {
    const approval = parseLimit(concessionRow.approval);
    const hard = parseLimit(concessionRow.hard);
    if (concessionFraction > hard)
      checks.push({
        status: "blocked",
        message: `Concession ${Math.round(concessionFraction * 100)}% exceeds the ${Math.round(hard * 100)}% hard limit`,
      });
    else if (concessionFraction > approval)
      checks.push({
        status: "approval",
        message: `Concession ${Math.round(concessionFraction * 100)}% over the ${Math.round(approval * 100)}% approval line`,
      });
  }

  const repeatRow = rows.find((r) => r.name.toLowerCase().includes("repeat"));
  if (repeatRow && scenario.priorPrice && scenario.priorPrice > 0) {
    const move = (quoted - scenario.priorPrice) / scenario.priorPrice;
    // Directional limits: an upward move is checked against the "up" magnitude,
    // a downward move against the "down" magnitude (falls back to symmetrical).
    const limit = (up: string, down?: string) =>
      move >= 0 ? parseLimit(up) : parseLimit(down ?? up);
    const breached = limit(repeatRow.approval, repeatRow.approvalDown);
    const hardLimit = limit(repeatRow.hard, repeatRow.hardDown);
    if (Math.abs(move) > hardLimit)
      checks.push({
        status: "blocked",
        message: `Repeat move ${pctLabel(move)} exceeds the ${pctLabel(move >= 0 ? hardLimit : -hardLimit)} limit`,
      });
    else if (Math.abs(move) > breached)
      checks.push({
        status: "approval",
        message: `Repeat move ${pctLabel(move)} over the ${pctLabel(move >= 0 ? breached : -breached)} approval line`,
      });
  }

  if (marginPct == null && concessionFraction === 0 && !scenario.priorPrice)
    return undefined;
  const status: QuoteVerdict["status"] = checks.some(
    (c) => c.status === "blocked",
  )
    ? "blocked"
    : checks.some((c) => c.status === "approval")
      ? "approval"
      : "ok";
  return { status, checks, marginPct };
}

// ── Speed (the Speed price rule, d65) ───────────────────────────────────────

/**
 * What the Speed price rule adds at `speedCode` over the standing price, read
 * as Standard's cheapest option (the simulator has one cost, as the backend's
 * `simulate_price_at_speed` does). Each faster Speed's floor is Standard plus
 * its uplift, never below a slower Speed's; Standard and Economy add nothing.
 */
function speedUplift(
  priceGroup: PriceGroup,
  speedCode: string,
  standingPrice: number,
): { amount: number; note: string | null; label: string } {
  const { ladder, rule, baselineCode } = priceGroup.speed;
  const at = ladder.findIndex((s) => s.speedCode === speedCode);
  const base = ladder.findIndex((s) => s.speedCode === baselineCode);
  const label = ladder[at]?.label ?? speedCode;
  if (at <= base) return { amount: 0, note: null, label };
  let floor = standingPrice;
  let note: string | null = null;
  for (const rung of ladder.slice(base + 1, at + 1)) {
    const entry = rule.entries.find((e) => e.speedCode === rung.speedCode);
    let uplift = entry ? standingPrice * entry.upliftPct : 0;
    if (entry?.minimumUpliftPence != null)
      uplift = Math.max(uplift, entry.minimumUpliftPence / 100);
    if (entry?.maximumUpliftPence != null)
      uplift = Math.min(uplift, entry.maximumUpliftPence / 100);
    const here = standingPrice + uplift;
    if (here >= floor) {
      floor = here;
      note = entry
        ? `+${Math.round(entry.upliftPct * 100)}% over Standard${
            entry.minimumUpliftPence != null
              ? `, at least £${(entry.minimumUpliftPence / 100).toFixed(2)}`
              : ""
          }`
        : null;
    } else {
      note = "never less than a slower Speed";
    }
  }
  return { amount: roundGbp(floor - standingPrice), note, label };
}

// ── Resolver ──────────────────────────────────────────────────────────────

export function getMockQuote(
  req: PriceQuoteRequest,
  priceGroupOverride?: PriceGroup,
): PriceQuote {
  const priceGroup =
    priceGroupOverride ??
    getMockPriceGroup(req.priceGroupId) ??
    getMockPriceGroup("standard")!;
  const item = getMockCatalogueItem(req.itemId);
  if (!item) {
    return {
      quoted: 0,
      perUnit: 0,
      priceGroupName: priceGroup.name,
      lines: [],
      standingPrice: 0,
    };
  }
  const scenario = req.scenario ?? {};

  const base = priceForQuantity(item.quantityBreaks, req.quantity).price;
  const optionsAdd = req.optionDeltas.reduce((sum, d) => sum + d.delta, 0);
  const priceBeforeStanding = base + optionsAdd;

  const lines: QuoteLine[] = [
    {
      label: `${item.name} ×${req.quantity.toLocaleString()}`,
      kind: "base",
      amount: base,
      source: "base",
      phase: "standing",
    },
  ];
  for (const d of req.optionDeltas) {
    lines.push({
      label: d.label,
      kind: "add",
      amount: d.delta,
      source: "option",
      phase: "standing",
    });
  }

  // Market (standing) — anchored on the competitor reference, by the item's stance.
  let appliedMarketAmt = 0;
  if (priceGroup.market.enabled && scenario.competitorPrice) {
    const { setting } = resolveStanceSetting(priceGroup, req.itemId);
    const cost =
      scenario.unitCost != null ? scenario.unitCost * req.quantity : undefined;
    const rec = marketRecommendation(
      setting,
      priceBeforeStanding,
      scenario.competitorPrice,
      cost,
    );
    const amount = roundGbp(rec.price - priceBeforeStanding);
    if (amount !== 0) {
      const applied = priceGroup.market.applyMode === "apply";
      if (applied) appliedMarketAmt = amount;
      const moveText =
        setting.stance === "win"
          ? `${Math.round((setting.dial ?? 0.06) * 100)}% below market`
          : setting.stance === "premium"
            ? `${Math.round((setting.dial ?? 0.05) * 100)}% above market`
            : setting.stance === "track"
              ? "match the market"
              : setting.stance === "optimise"
                ? "peak expected value"
                : "no move";
      lines.push({
        label: "Market",
        kind: amount >= 0 ? "add" : "discount",
        amount: Math.abs(amount),
        source: "market",
        applied,
        phase: "standing",
        detail: [
          { label: "Stance", value: stanceLabel(setting.stance) },
          {
            label: "Competitor",
            value: `£${Math.round(scenario.competitorPrice)}`,
          },
          { label: "Target", value: moveText },
        ],
      });
    }
  }

  const afterMarket = priceBeforeStanding + appliedMarketAmt;

  // Commitment (standing).
  const commitment = commitmentFor(priceGroup, item.productLabel);
  const commitmentAmt = commitment
    ? roundGbp(afterMarket * commitment.rate)
    : 0;
  if (commitment && commitmentAmt > 0) {
    lines.push({
      label: "Commitment",
      kind: "discount",
      amount: commitmentAmt,
      source: "commitment",
      phase: "standing",
      detail: [
        { label: "Applies to", value: commitment.productType },
        { label: "Rate", value: `−${Math.round(commitment.rate * 100)}%` },
      ],
    });
  }

  const standingPrice = afterMarket - commitmentAmt;

  // Order-time: Speed + capacity, layered on the standing price.
  const speed = speedUplift(priceGroup, req.speedCode, standingPrice);
  const premiumAmt = speed.amount;
  if (premiumAmt !== 0) {
    lines.push({
      label: "Speed",
      kind: "add",
      amount: premiumAmt,
      source: "speed",
      phase: "order",
      detail: [
        { label: "Speed", value: speed.label },
        ...(speed.note ? [{ label: "Rule", value: speed.note }] : []),
      ],
    });
  }

  const totalCost =
    scenario.unitCost != null
      ? roundGbp(scenario.unitCost * req.quantity)
      : null;
  const capacity = capacityMove(
    priceGroup,
    item,
    scenario.capacityLoad,
    totalCost,
  );
  const capacityAmt = capacity ? capacity.total : 0;
  if (capacity && capacityAmt !== 0) {
    const adjText = (frac: number) => (frac === 0 ? "0%" : pctLabel(frac));
    lines.push({
      label: "Capacity",
      kind: capacityAmt >= 0 ? "add" : "discount",
      amount: Math.abs(capacityAmt),
      source: "capacity",
      phase: "order",
      detail: [
        ...capacity.rows.map((r) => ({
          label: `${r.area} @ ${Math.round(r.load * 100)}%`,
          value: `£${r.areaCost.toLocaleString()} × ${adjText(r.fraction)} = ${signedGbp(r.amount)}`,
        })),
        {
          label: "On job cost",
          value: `£${(totalCost ?? 0).toLocaleString()}, split by area`,
        },
      ],
    });
  }

  const quoted = applyRounding(
    standingPrice + premiumAmt + capacityAmt,
    priceGroup.guardrails.rounding,
  );
  const perUnit = req.quantity > 0 ? quoted / req.quantity : 0;

  // Rebate is retrospective — illustrate it as a non-applied line.
  const rebate = rebateFor(priceGroup, scenario.spendToDate);
  if (rebate) {
    lines.push({
      label: "Rebate (retrospective)",
      kind: "discount",
      amount: rebate.earned,
      source: "rebate",
      applied: false,
      phase: "order",
      detail: [
        {
          label: `Spend to date (${rebate.period})`,
          value: `£${(scenario.spendToDate ?? 0).toLocaleString()}`,
        },
        {
          label: "Effective rate",
          value: `${(rebate.effectivePct * 100).toFixed(1)}% of spend`,
        },
      ],
    });
  }

  // Concession = the commitment give-backs only (discount + rebate). The market
  // move is a separate factor, already policed by the contribution floor; folding
  // it in here would double-count it and flag concession even with no commitment.
  const discountFraction =
    priceBeforeStanding > 0 ? commitmentAmt / priceBeforeStanding : 0;
  const concessionFraction =
    discountFraction + (rebate ? rebate.effectivePct : 0);
  const verdict = guardrailVerdict(
    priceGroup,
    quoted,
    req.quantity,
    scenario,
    concessionFraction,
  );

  return {
    quoted,
    perUnit,
    priceGroupName: priceGroup.name,
    lines,
    standingPrice,
    verdict,
  };
}
